import { DoseChangePanel } from '../../components/dtp/DoseChangePanel';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Package, Truck, ArrowUpRight, Repeat } from 'lucide-react';
import { dtpApi } from '../../lib/dtpApi';
import { dtpPath, useDtpParams } from '../../lib/clinicSite';
import { useDtpAuthStore } from '../../store/useDtpAuthStore';
import { BuyAgainButton, OrderProgress, OrderStatusPill, PayNowBanner, RefillTag, formatMoney } from '../../components/dtp/PortalOrderParts';
import { AutoRefillSheet } from '../../components/dtp/AutoRefillSheet';
import type { PortalOrder, RefillEligibility, RefillPlansResponse } from '../../types/refills';
import { track } from '../../lib/analytics';

export function DtpOrders() {
  const { physicianId } = useDtpParams();
  const patientId = useDtpAuthStore(s => s.patient?._id);
  const impersonated = useDtpAuthStore(s => s.impersonated);
  const [refillFor, setRefillFor] = useState<PortalOrder | null>(null);
  const orders = useQuery<PortalOrder[]>({
    queryKey: ['patient-orders', physicianId, patientId],
    retry: false,
    gcTime: 0,
    queryFn: async () => {
      const res = await dtpApi.getOrders();
      if (!res.success || !res.data) throw new Error('Orders unavailable');
      return ([...res.data] as PortalOrder[]).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    },
  });
  // Which received orders can go on auto-refill. Purely additive: if it fails,
  // the page still shows orders, just without the action.
  const eligibility = useQuery<Record<string, RefillEligibility>>({
    queryKey: ['refill-eligibility', physicianId, patientId],
    retry: false,
    enabled: !impersonated,
    queryFn: async () => {
      const res = await dtpApi.getRefillEligibility();
      return res.success && res.data ? res.data : {};
    },
  });
  const refillPlans = useQuery<RefillPlansResponse>({
    queryKey: ['refill-plans', physicianId, patientId],
    retry: false,
    enabled: !impersonated,
    queryFn: async () => {
      const res = await dtpApi.getRefillPlans();
      if (!res.success || !res.data) throw new Error('Refills unavailable');
      return res.data;
    },
  });
  const awaiting = orders.data?.filter((o: PortalOrder) => o.payUrl).length || 0;

  return (
    <div className="mx-auto max-w-3xl">
      <h1 className="portal-display text-[36px] font-bold leading-none sm:text-[44px]">Your orders</h1>
      <p className="mt-3 text-[17px] text-[var(--portal-muted)]">Track what’s on its way, and reorder in a tap.</p>
      {awaiting > 0 && (
        <p className="mt-4 inline-flex items-center gap-2 rounded-full bg-[var(--portal-tint-1)] px-3 py-1.5 text-sm font-semibold">
          {awaiting === 1 ? '1 order is waiting for payment' : `${awaiting} orders are waiting for payment`}
        </p>
      )}

      <DoseChangePanel readOnly={impersonated} />

      {orders.isPending ? (
        <div role="status" aria-label="Loading orders" className="mt-8 space-y-4">
          {[0, 1].map(i => <div key={i} className="portal-card h-48 motion-safe:animate-pulse" />)}
        </div>
      ) : orders.isError ? (
        <div role="alert" className="mt-8 rounded-2xl bg-[#fde4dc] p-4 text-sm text-[#8c2a14]">
          We couldn’t load your orders. <button className="min-h-[44px] font-semibold underline" onClick={() => orders.refetch()}>Try again</button>
        </div>
      ) : orders.data.length === 0 ? (
        <div className="portal-card mt-8 px-6 py-16 text-center">
          <span className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-[18px] bg-[var(--portal-tint-1)]"><Package aria-hidden="true" className="h-6 w-6" /></span>
          <p className="portal-display text-xl font-semibold">No orders yet</p>
          <p className="mt-2 text-sm text-[var(--portal-muted)]">Orders you place with your clinic show up here, with tracking.</p>
          <Link to={dtpPath(physicianId, '/shop')} className="mt-6 inline-flex h-10 items-center gap-2 rounded-full bg-[var(--portal-primary)] px-4 text-sm font-semibold text-[var(--portal-primary-fg)] hover:opacity-90">Browse treatments<ArrowUpRight aria-hidden="true" className="h-4 w-4" /></Link>
        </div>
      ) : (
        <ol className="mt-8 space-y-4">
          {orders.data.map((order: PortalOrder) => <li key={order._id as string}>
            <OrderCard order={order} refill={eligibility.data?.[order._id as string]}
              canSetUpRefill={!!refillPlans.data} onAutoRefill={() => setRefillFor(order)} />
          </li>)}
        </ol>
      )}

      {refillFor && refillPlans.data && (
        <AutoRefillSheet order={refillFor} requiredConsents={refillPlans.data.requiredConsents} onClose={() => setRefillFor(null)} />
      )}
    </div>
  );
}

function OrderCard({ order, refill, canSetUpRefill, onAutoRefill }: {
  order: PortalOrder;
  refill?: RefillEligibility;
  canSetUpRefill: boolean;
  onAutoRefill: () => void;
}) {
  const { physicianId } = useDtpParams();
  const tracking = order.trackingInfo;
  return (
    <article aria-labelledby={`order-${order._id}`} className="portal-card p-6 sm:p-7">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id={`order-${order._id}`} className="portal-display text-xl font-bold tabular-nums">#{order.orderNumber}</h2>
          <p className="mt-0.5 text-sm text-[var(--portal-muted)]">{new Date(order.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2"><RefillTag order={order} /><OrderStatusPill status={order.status} /></div>
      </header>

      {order.payUrl && <div className="mt-5"><PayNowBanner order={order} /></div>}

      <div className="mt-5"><OrderProgress status={order.status} /></div>

      <ul className="mt-5 divide-y divide-[var(--portal-line)]">
        {order.items?.map((item, index) => (
          <li key={`${item.productId}-${index}`} className="flex items-center justify-between gap-4 py-3">
            <span className="flex min-w-0 items-center gap-3">
              <span aria-hidden="true" className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[var(--portal-tint-2)] text-sm font-semibold">{item.productName?.trim()?.[0]?.toUpperCase()}</span>
              <span className="truncate text-[15px]">{item.productName}{item.quantity > 1 && <span className="text-[var(--portal-muted)]"> × {item.quantity}</span>}</span>
            </span>
            <span className="shrink-0 text-[15px] tabular-nums text-[var(--portal-muted)]">{formatMoney(item.totalPrice)}</span>
          </li>
        ))}
      </ul>

      <footer className="mt-2 flex flex-wrap items-center justify-between gap-3 border-t border-[var(--portal-line)] pt-5">
        <p className="portal-display text-lg font-semibold tabular-nums">Total {formatMoney(order.totalAmount)}</p>
        <div className="flex flex-wrap items-start gap-2">
          {tracking?.trackingUrl && (
            <a href={tracking.trackingUrl} target="_blank" rel="noopener noreferrer" onClick={() => track('order_tracking_clicked', { orderId: order._id })}
              className="inline-flex h-10 items-center gap-2 rounded-full bg-white/80 px-4 text-sm font-semibold ring-1 ring-[var(--portal-line)] transition hover:bg-white">
              <Truck aria-hidden="true" className="h-4 w-4" />Track{tracking.carrier ? ` with ${tracking.carrier}` : ' package'}
            </a>
          )}
          {refill?.eligible && canSetUpRefill && (
            <button type="button" onClick={() => { track('auto_refill_setup_opened', { placement: 'orders' }); onAutoRefill(); }}
              className="inline-flex h-10 items-center gap-2 rounded-full bg-white/80 px-4 text-sm font-semibold ring-1 ring-[var(--portal-line)] transition hover:bg-white">
              <Repeat aria-hidden="true" className="h-4 w-4" />Set up auto-refill
            </button>
          )}
          {refill?.reason === 'already_on_plan' && (
            <Link to={dtpPath(physicianId, '/refills')}
              className="inline-flex h-10 items-center gap-2 rounded-full bg-[var(--portal-tint-3)] px-4 text-sm font-semibold transition hover:opacity-90">
              <Repeat aria-hidden="true" className="h-4 w-4" />On auto-refill
            </Link>
          )}
          {!order.payUrl && <BuyAgainButton order={order} />}
        </div>
        {/* Say why auto-refill isn't offered instead of silently omitting the
            button — e.g. GLP-1 program products need a health check each order. */}
        {refill && !refill.eligible && refill.reason !== 'already_on_plan' && refill.message && canSetUpRefill && (
          <p className="basis-full text-sm text-[var(--portal-muted)]">{refill.message}</p>
        )}
      </footer>
    </article>
  );
}
